import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV_LOGO } from "@/lib/assets";

const desktopLogoLeft = "max(2rem, calc((100vw - 80rem) / 2 + 2rem))";

export default function Navigation({ onBook }) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleScroll = (id) => {
    setMenuOpen(false);
    const el = document.getElementById(id);

    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }

    navigate("/");
    setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }, 200);
  };

  const linkColor = scrolled ? "#1C1917" : "#FDF8ED";

  const logoEl = (
    <>
      <img
        src={NAV_LOGO}
        alt="Shihab Romeed"
        style={{
          height: "32px",
          width: "auto",
          maxWidth: "185px",
          display: "block",
          objectFit: "contain",
          objectPosition: "left center",
          mixBlendMode: scrolled ? "multiply" : "screen",
          filter: scrolled ? "invert(1)" : "none",
          transition: "filter 0.45s ease",
        }}
      />
      <span
        style={{
          display: "block",
          marginTop: "0px",
          height: "2.5px",
          width: "100%",
          background:
            "linear-gradient(90deg, transparent 0%, #D4AF37 22%, #C49A2A 42%, #AA8820 50%, #C49A2A 58%, #D4AF37 78%, transparent 100%)",
          transformOrigin: "center",
          animation: "pulse-underline 8.5s ease-in-out infinite",
        }}
      />
    </>
  );

  return (
    <nav
      data-testid="navigation"
      className="fixed top-0 left-0 right-0 z-50 overflow-visible"
      style={{
        background: scrolled ? "rgba(255,255,255,0.78)" : "transparent",
        backdropFilter: scrolled ? "blur(20px)" : "none",
        WebkitBackdropFilter: scrolled ? "blur(20px)" : "none",
        transition: "background 0.5s ease, backdrop-filter 0.5s ease",
      }}
    >
      <div
        className="hidden md:block absolute top-8"
        style={{
          left: scrolled ? desktopLogoLeft : "50%",
          transform: scrolled ? "translate(0, -50%)" : "translate(-50%, -50%)",
          transition: "left 0.65s cubic-bezier(0.4, 0, 0.2, 1), transform 0.65s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <Link to="/" data-testid="nav-logo" className="flex flex-col items-start shrink-0 self-center">
          {logoEl}
        </Link>
      </div>

      <div className="hidden md:flex max-w-7xl mx-auto px-8 h-16 items-center justify-end">
        <div
          className="flex items-center gap-8"
          style={{
            opacity: scrolled ? 1 : 0,
            transform: scrolled ? "translateY(0)" : "translateY(-10px)",
            pointerEvents: scrolled ? "auto" : "none",
            transition: "opacity 0.45s ease 0.08s, transform 0.45s ease 0.08s",
          }}
        >
          {[{ label: "Practice", id: "practice" }, { label: "Academy", id: "academy" }].map(
            ({ label, id }) => (
              <button
                key={id}
                data-testid={`nav-${id}`}
                onClick={() => handleScroll(id)}
                className="font-outfit text-sm tracking-wide hover:opacity-60 whitespace-nowrap transition-opacity duration-200"
                style={{ color: linkColor }}
              >
                {label}
              </button>
            ),
          )}
          <Link
            to="/about"
            data-testid="nav-about"
            className="font-outfit text-sm tracking-wide hover:opacity-60 whitespace-nowrap transition-opacity duration-200"
            style={{ color: linkColor }}
          >
            About
          </Link>
          <button
            data-testid="nav-book-btn"
            onClick={() => onBook(null)}
            className="font-outfit text-sm font-medium px-5 py-2 whitespace-nowrap transition-all duration-200 hover:opacity-90 active:scale-95"
            style={{
              background: "#D4AF37",
              color: "#16110F",
            }}
          >
            Book Appointment
          </button>
        </div>
      </div>

      <div className="md:hidden relative h-16 flex items-center justify-center px-4">
        <Link to="/" data-testid="nav-logo-mobile" className="flex flex-col items-start self-center">
          {logoEl}
        </Link>

        <button
          data-testid="nav-mobile-menu-btn"
          className="absolute right-4 top-1/2 -translate-y-1/2 p-2"
          style={{ color: scrolled ? "#1C1917" : "#FDF8ED" }}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          {menuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {menuOpen && (
        <div
          data-testid="nav-mobile-menu"
          className="md:hidden border-b border-stone-200 px-4 py-5 flex flex-col gap-0"
          style={{ background: scrolled ? "rgba(255,255,255,0.98)" : "rgba(22,17,15,0.97)" }}
        >
          {[
            { label: "Practice - Specialist Dental London", action: () => handleScroll("practice") },
            { label: "Academy - London Dental Academy", action: () => handleScroll("academy") },
          ].map(({ label, action }) => (
            <button
              key={label}
              onClick={action}
              className="font-outfit text-sm text-left py-3.5 border-b w-full"
              style={{ color: linkColor, borderColor: scrolled ? "#F0EFED" : "#3A2E28" }}
            >
              {label}
            </button>
          ))}
          <Link
            to="/about"
            onClick={() => setMenuOpen(false)}
            className="font-outfit text-sm py-3.5 border-b block"
            style={{ color: linkColor, borderColor: scrolled ? "#F0EFED" : "#3A2E28" }}
          >
            About Dr. Romeed
          </Link>
          <button
            data-testid="nav-mobile-book-btn"
            onClick={() => {
              setMenuOpen(false);
              onBook(null);
            }}
            className="font-outfit text-sm font-medium py-3.5 mt-4 text-center w-full"
            style={{ background: "#D4AF37", color: "#16110F" }}
          >
            Book Appointment
          </button>
        </div>
      )}
    </nav>
  );
}
